// מסך 11 — טבלת קישורים מתויגים: כתובת סופית לכל באנר, עריכת key/URL עם diff, יצירה עם מדיניות UTM קיים, תבנית תיוג.
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { buildTrackingUrl, findDuplicates, slugifyKey, type ExistingUtmPolicy } from '@bb/schema';
import { api } from '../api';
import { Empty, ErrorBox, Field, Loading, useLoad, useMe } from '../components/ui';
import { CopyButton, PageHead, Tabs, useAction } from '../components/mgmt/common';
import { TemplateEditor, useTemplates } from '../components/mgmt/TrackingTemplateEditor';
import { isUrl } from '../components/mgmt/LinksEditor';
import type { CampaignFull } from '../components/mgmt/CampaignParts';

interface Row {
  variantId: string; setId?: string; setName?: string; mediaName: string; mediaKey: string; placementName: string; displayName: string; bannerKey: string;
  width: number; height: number; baseUrl: string; resolvedUrl: string | null; status: 'ok' | 'conflict' | 'error'; issues: string[];
  conflicts: { key: string; existing: string; generated: string }[]; templateVersion?: number; saved?: boolean;
}
type Tab = 'links' | 'template';
const STATUS: Record<string, { l: string; c: string }> = { ok: { l: 'תקין', c: 'ok' }, conflict: { l: 'UTM קיים — נדרשת בחירה', c: 'warn' }, error: { l: 'שגיאה', c: 'err' } };

export default function TrackingLinks() {
  const { id } = useParams();
  const me = useMe();
  const canEdit = me.role !== 'viewer';
  const [tab, setTab] = useState<Tab>('links');
  const camp = useLoad<CampaignFull>(`/api/campaigns/${id}`);
  const links = useLoad<{ rows: Row[] }>(`/api/campaigns/${id}/tracking-links`);
  const tpl = useTemplates(id!);
  const [rows, setRows] = useState<Row[] | null>(null);
  const shown = rows ?? links.data?.rows ?? null;
  const [policy, setPolicy] = useState<ExistingUtmPolicy>('ask');
  const [sel, setSel] = useState<string[]>([]);
  const gen = useAction();
  const [genInfo, setGenInfo] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const generate = async () => {
    const r = await gen.run(() => api.post<{ rows: Row[]; skipped: number }>(`/api/campaigns/${id}/tracking-links/generate`, { existingUtmPolicy: policy, variantIds: sel.length ? sel : undefined }));
    if (r) {
      setRows(r.rows);
      setGenInfo(r.skipped ? `נשמרו ${r.rows.length - r.skipped} קישורים; ${r.skipped} לא נשמרו (סתירת UTM או שגיאה) — ראו בטבלה.` : `נשמרו ${r.rows.length} קישורים.`);
    }
  };
  const reload = () => { setRows(null); links.reload(); };

  const filtered = (shown ?? []).filter((r) => !q || [r.mediaName, r.placementName, r.displayName, r.bannerKey].some((x) => x.toLowerCase().includes(q.toLowerCase())));
  const c = camp.data?.campaign;

  return (
    <div>
      <PageHead title="קישורים מתויגים"
        crumbs={c ? <><Link to={`/clients/${c.client_id}`}>לקוח</Link> / <Link to={`/campaigns/${c.id}`}>{c.name}</Link> / קישורים</> : undefined}
        sub={c ? <>campaignKey: <span className="mono">{c.campaign_key}</span> · URL ברירת מחדל: <span className="ltr">{c.default_url || 'לא הוגדר'}</span></> : undefined} />
      <div className="alert info small" style={{ marginBottom: 10 }}>התיוג דטרמיניסטי וללא AI. הכתובת הסופית נכנסת ל-click adapter של המדיה וננעלת ביצוא. <strong>תיוג לבדו אינו מוכיח קליק או ליד.</strong></div>
      <Tabs<Tab> label="קישורים" value={tab} onChange={setTab} tabs={[{ id: 'links', label: `טבלת קישורים (${shown?.length ?? 0})` }, { id: 'template', label: `תבנית תיוג${tpl.campaign ? ' (דריסה)' : ''}` }]} />
      {tab === 'template' && <div className="card"><TemplateEditor campaignId={id!} tpl={tpl} admin={me.role === 'admin'} onSaved={reload} /></div>}
      {tab === 'links' && (
        <div className="stack">
          {canEdit && (
            <div className="card row" style={{ alignItems: 'flex-end' }}>
              <Field label="UTM שכבר קיים בכתובת">
                <select value={policy} onChange={(e) => setPolicy(e.target.value as ExistingUtmPolicy)}>
                  <option value="ask">לשאול — להציג סתירה ולא לשמור</option>
                  <option value="replace">להחליף בערך המחושב</option>
                  <option value="keep">לשמור את הקיים</option>
                </select>
              </Field>
              <button className="btn primary" onClick={generate} disabled={gen.busy || !shown?.length}>{gen.busy ? 'מייצר…' : sel.length ? `יצירה ושמירה ל-${sel.length} נבחרים` : 'יצירה ושמירה לכל הבאנרים'}</button>
              <Field label="חיפוש"><input type="search" value={q} onChange={(e) => setQ(e.target.value)} /></Field>
              <button className="btn" onClick={reload}>רענון</button>
            </div>
          )}
          <div aria-live="polite">{genInfo && <div className="alert ok small">{genInfo}</div>}<ErrorBox error={gen.error} /></div>
          <ErrorBox error={links.error} />
          {links.loading && !shown && <Loading />}
          {shown && shown.length === 0 && <Empty>אין באנרים בקמפיין. צרו סט כדי לקבל קישורים.</Empty>}
          {filtered.length > 0 && (
            <table className="table cards-sm">
              <thead><tr>
                {canEdit && <th><input type="checkbox" aria-label="בחירת הכל" checked={sel.length === filtered.length} onChange={(e) => setSel(e.target.checked ? filtered.map((r) => r.variantId) : [])} /></th>}
                <th>מדיה</th><th>מיקום</th><th>שם באנר</th><th>רוחב</th><th>גובה</th><th>כתובת סופית</th><th>סטטוס</th><th></th>
              </tr></thead>
              <tbody>{filtered.map((r) => (
                <RowView key={r.variantId} r={r} canEdit={canEdit} selected={sel.includes(r.variantId)} onSelect={(v) => setSel((s) => (v ? [...s, r.variantId] : s.filter((x) => x !== r.variantId)))}
                  editing={editing === r.variantId} onEdit={() => setEditing(editing === r.variantId ? null : r.variantId)} />
              ))}</tbody>
            </table>
          )}
          {editing && c && shown && (
            <EditPanel key={editing} row={shown.find((r) => r.variantId === editing)!} all={shown} campaignKey={c.campaign_key} defaultUrl={c.default_url} template={tpl.effective}
              onClose={() => setEditing(null)} onSaved={(nr) => { setRows((shown ?? []).map((x) => (x.variantId === nr.variantId ? nr : x))); }} />
          )}
        </div>
      )}
    </div>
  );
}

function RowView({ r, canEdit, selected, onSelect, editing, onEdit }: { r: Row; canEdit: boolean; selected: boolean; onSelect: (v: boolean) => void; editing: boolean; onEdit: () => void }) {
  const st = STATUS[r.status] ?? { l: r.status, c: '' };
  return (
    <tr style={editing ? { background: 'var(--accent-soft)' } : undefined}>
      {canEdit && <td><input type="checkbox" aria-label={`בחירת ${r.displayName}`} checked={selected} onChange={(e) => onSelect(e.target.checked)} /></td>}
      <td data-l="מדיה">{r.mediaName}<div className="mono small muted">{r.mediaKey}</div></td>
      <td data-l="מיקום">{r.placementName}</td>
      <td data-l="שם באנר">{r.displayName}<div className="mono small muted">{r.bannerKey}</div></td>
      <td data-l="רוחב" className="ltr">{r.width}</td>
      <td data-l="גובה" className="ltr">{r.height}</td>
      <td data-l="כתובת סופית">
        {r.resolvedUrl ? <div className="row" style={{ gap: 4, alignItems: 'flex-start' }}><span className="url-cell">{r.resolvedUrl}</span><CopyButton text={r.resolvedUrl} /></div> : <span className="unknown">אין כתובת</span>}
      </td>
      <td data-l="סטטוס">
        <span className={`badge ${st.c}`}>{st.l}</span>
        {r.saved === false && r.status === 'ok' && <> <span className="badge warn">לא נשמר</span></>}
        {r.saved && <> <span className="badge info">נשמר</span></>}
        {r.issues.length > 0 && <ul className="small" style={{ margin: '4px 0 0', paddingInlineStart: 16 }}>{r.issues.map((x, i) => <li key={i}>{x}</li>)}</ul>}
        {r.conflicts.length > 0 && (
          <div className="small" style={{ marginTop: 4 }}>
            {r.conflicts.map((c) => <div key={c.key}><span className="mono">{c.key}</span>: קיים <span className="mono diff-before">{c.existing}</span> / מחושב <span className="mono diff-after">{c.generated}</span></div>)}
            <div className="muted">בחרו "להחליף" או "לשמור את הקיים" ויצרו שוב.</div>
          </div>
        )}
      </td>
      <td>{canEdit && <button className="btn small" onClick={onEdit} aria-expanded={editing}>{editing ? 'סגירה' : 'עריכה'}</button>}</td>
    </tr>
  );
}

function EditPanel({ row, all, campaignKey, defaultUrl, template, onClose, onSaved }: { row: Row; all: Row[]; campaignKey: string; defaultUrl: string; template: ReturnType<typeof useTemplates>['effective']; onClose: () => void; onSaved: (r: Row) => void }) {
  const inherited = !row.baseUrl || row.baseUrl === defaultUrl;
  const [bannerKey, setBannerKey] = useState(row.bannerKey);
  const [useOwnUrl, setUseOwnUrl] = useState(!inherited);
  const [baseUrl, setBaseUrl] = useState(inherited ? '' : row.baseUrl);
  const [result, setResult] = useState<{ diff: { before: string | null; after: string | null; changed: boolean }; exportsNeedRebuild: boolean } | null>(null);
  const { busy, error, run } = useAction();
  const keyOk = slugifyKey(bannerKey) === bannerKey;
  const effectiveBase = useOwnUrl ? baseUrl : defaultUrl;
  const urlBad = useOwnUrl && baseUrl !== '' && !isUrl(baseUrl);
  // תצוגה חיה בצד הלקוח — אותו מחולל כמו בשרת
  const preview = useMemo(() => {
    const dups = findDuplicates(all.map((r) => ({ variantId: r.variantId, mediaKey: r.mediaKey, bannerKey: r.variantId === row.variantId ? bannerKey : r.bannerKey, width: r.width, height: r.height })));
    return buildTrackingUrl({ baseUrl: effectiveBase || '', campaignKey, mediaKey: row.mediaKey, bannerKey, width: row.width, height: row.height, variantId: row.variantId, disambiguate: dups.has(row.variantId) }, template, 'keep');
  }, [effectiveBase, bannerKey, all, row, campaignKey, template]);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const body: { bannerKey?: string; baseUrl?: string | null } = {};
    if (bannerKey !== row.bannerKey) body.bannerKey = bannerKey;
    const newBase = useOwnUrl ? baseUrl : null;
    if ((inherited ? null : row.baseUrl) !== newBase) body.baseUrl = newBase;
    const r = await run(() => api.patch<{ row: Row; diff: { before: string | null; after: string | null; changed: boolean }; exportsNeedRebuild: boolean }>(`/api/variants/${row.variantId}/tracking`, body), 'הקישור עודכן');
    if (r) { setResult(r); onSaved(r.row); }
  };
  return (
    <section className="card stack" aria-label={`עריכת קישור — ${row.displayName}`}>
      <div className="row between"><h2 style={{ margin: 0 }}>עריכת קישור — {row.displayName} <span className="ltr small muted">{row.width}×{row.height}</span></h2><button className="btn small" onClick={onClose}>סגירה</button></div>
      <form className="stack" onSubmit={save}>
        <div className="grid cols-2">
          <Field label="bannerKey (utm_content)" hint="מפתח מעקב יציב; שינוי שם תצוגה לא משנה אותו">
            <input className="ltr mono" value={bannerKey} onChange={(e) => setBannerKey(e.target.value)} aria-invalid={!keyOk} />
          </Field>
          <div className="stack" style={{ gap: 4 }}>
            <label className="checkline"><input type="checkbox" checked={useOwnUrl} onChange={(e) => setUseOwnUrl(e.target.checked)} /> URL ייעודי לבאנר (במקום ברירת המחדל של הקמפיין)</label>
            {useOwnUrl ? <input aria-label="URL בסיס לבאנר" className="ltr" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://" aria-invalid={urlBad} />
              : <span className="small muted ltr">{defaultUrl || 'לא הוגדר URL בקמפיין'}</span>}
          </div>
        </div>
        {!keyOk && <div className="small" style={{ color: 'var(--danger)' }}>מפתח לא תקין. הצעה: <button type="button" className="btn small" onClick={() => setBannerKey(slugifyKey(bannerKey))}><span className="mono">{slugifyKey(bannerKey)}</span></button></div>}
        {urlBad && <div className="small" style={{ color: 'var(--danger)' }}>כתובת לא תקינה</div>}
        <div aria-live="polite">
          <div className="small muted">תצוגה מקדימה (לפני שמירה, תבנית גרסה {template.version}):</div>
          {preview.resolvedUrl ? <div className="url-cell" style={{ maxWidth: 'none' }}>{preview.resolvedUrl}</div> : <div className="small" style={{ color: 'var(--danger)' }}>{preview.errors.join(' · ') || 'אין כתובת'}</div>}
          {preview.warnings.map((w, i) => <div key={i} className="small" style={{ color: 'var(--warn)' }}>{w}</div>)}
          {preview.resolvedUrl !== row.resolvedUrl && row.resolvedUrl && <div className="small" style={{ marginTop: 4 }}><span className="diff-before url-cell" style={{ display: 'inline' }}>{row.resolvedUrl}</span></div>}
        </div>
        <ErrorBox error={error} />
        <div className="row"><button className="btn primary" disabled={busy || !keyOk || urlBad || (useOwnUrl && !baseUrl)}>{busy ? 'שומר…' : 'שמירה'}</button></div>
      </form>
      {result && (
        <div className="stack" role="status" aria-live="polite">
          {result.diff.changed ? (
            <div className="alert info small">
              <div>לפני: <span className="url-cell diff-before" style={{ display: 'inline' }}>{result.diff.before ?? '—'}</span></div>
              <div>אחרי: <span className="url-cell diff-after" style={{ display: 'inline' }}>{result.diff.after ?? '—'}</span></div>
            </div>
          ) : <div className="alert ok small">הכתובת הסופית לא השתנתה.</div>}
          {result.exportsNeedRebuild && <div className="alert warn"><strong>יצואים קיימים צריכים יצירה מחדש.</strong> ה-ZIP הקיים נשאר עם הכתובת הישנה; הבאנר סומן לבדיקה. <Link to="/exports">למרכז היצוא</Link></div>}
        </div>
      )}
    </section>
  );
}
